"use strict";
/**
 * Base-class for any MHub client.
 *
 * Derived classes add actual transport logic to connect to
 * e.g. a Node.JS websocket API, a browser version, or a version
 * specifically for testing.
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
var assert = require("assert");
var events = require("events");
var ts_promise_1 = require("ts-promise");
var message_1 = require("./message");
var protocol = require("./protocol");
var MAX_SEQ = 65536;
exports.defaultClientOptions = {
    keepalive: 30000,
};
/**
 * Abstract MHub client.
 *
 * Implements MHub client protocol, but does not implement the transport layer
 * such as WebSocket, raw TCP, etc.
 *
 * @event open() Emitted when connection was established.
 * @event close() Emitted when connection was closed.
 * @event error(e: Error) Emitted when there was a connection, server or protocol error.
 * @event message(m: Message, subscriptionId: string) Emitted when message was received (due to subscription).
 */
var BaseClient = /** @class */ (function (_super) {
    __extends(BaseClient, _super);
    /**
     * Create new BaseClient.
     * @param options Protocol settings
     */
    function BaseClient(socketConstructor, options) {
        var _this = _super.call(this) || this;
        _this._transactions = {};
        _this._seqNo = 0;
        _this._idleTimer = undefined;
        _this._connected = false; // Prevent emitting `close` when not connected
        // Ensure options is an object and fill in defaults
        options = __assign({}, exports.defaultClientOptions, options);
        _this._options = options;
        _this._socketConstructor = socketConstructor;
        return _this;
    }
    /**
     * Connect to the MServer.
     * If connection is already active or pending, this is a no-op.
     * Note: a connection is already initiated when the constructor is called.
     */
    BaseClient.prototype.connect = function () {
        var _this = this;
        if (this._connected) {
            return ts_promise_1.default.resolve();
        }
        if (this._closing) {
            return this.close().then(function () { return _this.connect(); });
        }
        if (!this._connecting) {
            this._connecting = new ts_promise_1.default(function (resolve, reject) {
                if (!_this._socket) {
                    var socketConstructor = _this._socketConstructor;
                    _this._socket = socketConstructor(); // call it without a `this`
                    _this._socket.on("error", function (e) { _this._handleSocketError(e); });
                    _this._socket.on("open", function () { _this._handleSocketOpen(); });
                    _this._socket.on("close", function () { _this._handleSocketClose(); });
                    _this._socket.on("message", function (data) { _this._handleSocketMessage(data); });
                }
                _this._socket.once("open", resolve);
                _this._socket.once("error", reject);
            }).finally(function () {
                _this._connecting = undefined;
            });
        }
        return this._connecting;
    };
    /**
     * Disconnect from MServer.
     * Pending requests will be rejected with an error.
     * If already disconnected, this becomes a no-op.
     *
     * Note: any existing subscriptions will be lost.
     *
     * Optionally pass an error to signal abrupt failure,
     * forcefully terminating the connection.
     * The same error will be used to reject any pending
     * requests.
     * @param error (optional) Error to emit, reject transactions with, and
     *              forcefully close connection.
     */
    BaseClient.prototype.close = function (error) {
        var _this = this;
        if (!this._closing) {
            this._closing = new ts_promise_1.default(function (resolve) {
                // Announce error if necessary
                if (error) {
                    _this._asyncEmit("error", error);
                }
                // Abort pending transactions
                var transactionError = error || new Error("connection closed");
                for (var t in _this._transactions) {
                    if (!_this._transactions[t]) {
                        continue;
                    }
                    _this._transactions[t](ts_promise_1.default.reject(transactionError));
                }
                _this._transactions = {};
                if (_this._socket) {
                    if (error || !_this._connected) {
                        // Forcefully close in case of an error, or when
                        // not connected yet (otherwise we may have to wait
                        // until the connect times out).
                        return resolve(_this._socket.terminate());
                    }
                    else {
                        // Gracefully close in normal cases, meaning any
                        // in-progress writes will be completed first.
                        return resolve(_this._socket.close());
                    }
                }
                else {
                    resolve(undefined);
                }
            }).finally(function () {
                _this._socket = undefined;
                _this._closing = undefined;
            });
        }
        return this._closing;
    };
    /**
     * Login to server using username/password.
     *
     * Warning: the username and password are sent in plain text.
     * Only use this on secure connections such as wss://.
     *
     * @param username Username.
     * @param password Password.
     */
    BaseClient.prototype.login = function (username, password) {
        return this._send({
            type: "login",
            username: username,
            password: password,
        }).return();
    };
    /**
     * Subscribe to a node.
     *
     * Emits the "message" event when a message is received for this subscription.
     * First argument of that event is the message, second is the subscription id
     * (or "default" if no id was given).
     *
     * @param nodeName Name of node in MServer to subscribe to (e.g. "default")
     * @param pattern  Optional pattern glob (e.g. "/some/foo*"). Matches all topics if omitted.
     * @param id       Optional subscription ID sent back with all matching messages
     */
    BaseClient.prototype.subscribe = function (nodeName, pattern, id) {
        return this._send({
            type: "subscribe",
            node: nodeName,
            pattern: pattern,
            id: id,
        }).return();
    };
    /**
     * Unsubscribe `pattern` (or all if omitted) from given `node` and `id`.
     * Subscription id "default" is used if `id` is omitted.
     *
     * @param nodeName Name of node in MServer to unsubscribe from (e.g. "default")
     * @param pattern  Optional pattern glob (e.g. "/some/foo*"). Unsubscribes all (on `node` and `id`)
     *                 if omitted.
     * @param id       Subscription ID, or "default"
     */
    BaseClient.prototype.unsubscribe = function (nodeName, pattern, id) {
        return this._send({
            type: "unsubscribe",
            node: nodeName,
            pattern: pattern,
            id: id,
        }).return();
    };
    // Implementation
    BaseClient.prototype.publish = function (nodeName) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var message;
        if (typeof args[0] === "object") {
            message = args[0];
        }
        else {
            message = new message_1.default(args[0], args[1], args[2]);
        }
        message.validate();
        return this._send({
            type: "publish",
            node: nodeName,
            topic: message.topic,
            data: message.data,
            headers: message.headers,
        }).return();
    };
    /**
     * Ping server.
     * Mostly used to check whether connection is still alive.
     * Note that the client will automatically send pings in the
     * absence of other communication, so there should be no need to
     * manually send pings.
     *
     * @param timeout (optional) Timeout in milliseconds before rejecting
     *                the promise with an error, or infinite if not given.
     */
    BaseClient.prototype.ping = function (timeout) {
        var pingResult = this._send({
            type: "ping",
        }).return();
        if (timeout) {
            return ts_promise_1.default.race([
                ts_promise_1.default.delay(timeout).throw(new Error("ping timeout")),
                pingResult,
            ]);
        }
        else {
            return pingResult;
        }
    };
    /**
     * Defer calling of events to next tick, to prevent e.g. errors
     * in handlers from interfering with client state, and to
     * prevent hard-to-debug async weirdness.
     */
    BaseClient.prototype._asyncEmit = function (event) {
        var _this = this;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        ts_promise_1.default.resolve().done(function () {
            _this.emit.apply(_this, [event].concat(args));
        });
    };
    BaseClient.prototype._handleSocketOpen = function () {
        this._connected = true;
        this._asyncEmit("open");
        this._restartIdleTimer();
    };
    BaseClient.prototype._handleSocketError = function (err) {
        if (!(err instanceof Error)) {
            err = new Error("WebSocket error: " + err);
        }
        this._asyncEmit("error", err);
    };
    BaseClient.prototype._handleSocketClose = function () {
        if (this._connected) {
            this._connected = false;
            // Emit `close` event when socket is closed (i.e. not just when
            // `close()` is called without being connected yet)
            this._asyncEmit("close");
        }
        // Discard socket, abort pending transactions
        this.close();
        this._stopIdleTimer();
    };
    BaseClient.prototype._handleSocketMessage = function (data) {
        try {
            if (!data || typeof data !== "object") {
                throw new Error("missing or invalid data received");
            }
            var response = data;
            if (typeof response.type !== "string") {
                throw new Error("missing type property on received data");
            }
            switch (response.type) {
                case "message":
                    var msgRes = response;
                    var message = new message_1.default(msgRes.topic, msgRes.data, msgRes.headers);
                    message.validate();
                    this._asyncEmit("message", message, msgRes.subscription);
                    break;
                case "error":
                    var errRes = response;
                    var err = new Error("server error: " + errRes.message);
                    if (errRes.seq === undefined || !this._release(errRes.seq, err, response)) {
                        // Emit as a generic error when it could not be attributed to
                        // a specific request
                        this._asyncEmit("error", err);
                    }
                    break;
                case "suback":
                case "unsuback":
                case "puback":
                case "loginack":
                    var ackDec = response;
                    if (protocol.hasSequenceNumber(ackDec)) {
                        this._release(ackDec.seq, undefined, ackDec);
                    }
                    break;
                case "pingack":
                    var pingDec = response;
                    if (protocol.hasSequenceNumber(pingDec)) { // ignore 'gratuitous' pings from the server
                        this._release(pingDec.seq, undefined, pingDec);
                    }
                    break;
                default:
                    throw new Error("unknown message type: " + response.type);
            }
            this._restartIdleTimer();
        }
        catch (e) {
            this._asyncEmit("error", new Error("message decode error: " + e.message));
        }
    };
    /**
     * (Re-)start idle timer and send pings when connection is idle
     * for too long.
     */
    BaseClient.prototype._restartIdleTimer = function () {
        var _this = this;
        this._stopIdleTimer();
        if (!this._socket) {
            return;
        }
        if (typeof this._options.keepalive !== "number" || this._options.keepalive <= 0) {
            return;
        }
        this._idleTimer = setTimeout(function () {
            _this._idleTimer = undefined;
            _this._handleIdleTimeout();
        }, this._options.keepalive);
    };
    BaseClient.prototype._stopIdleTimer = function () {
        if (this._idleTimer !== undefined) {
            clearTimeout(this._idleTimer);
            this._idleTimer = undefined;
        }
    };
    BaseClient.prototype._handleIdleTimeout = function () {
        var _this = this;
        if (!this._socket || !this._connected) {
            return;
        }
        this.ping(this._options.keepalive)
            .catch(function (e) {
            if (e && e.message === "server error: unknown node 'undefined'") {
                // Older MHub didn't support ping, so ignore this error.
                // (Additionally, all then-existing commands had to refer to a node.)
                // TCP machinery will terminate the connection if needed.
                // (Only doesn't work if this goes through proxies, and the
                // connection after that is dead.)
                return;
            }
            if (_this._connected) {
                // Only close (and emit an error) when we (seemed to be)
                // succesfully connected (i.e. prevent multiple errors).
                _this.close(e);
            }
        });
    };
    BaseClient.prototype._send = function (msg) {
        var _this = this;
        return new ts_promise_1.default(function (resolve, reject) {
            var seq = _this._nextSeq();
            msg.seq = seq;
            _this._transactions[seq] = resolve;
            if (!_this._socket || !_this._connected) {
                throw new Error("not connected");
            }
            _this._restartIdleTimer();
            _this._socket.send(msg).catch(function (err) {
                if (err) {
                    _this._release(seq, err);
                    return reject(err);
                }
            });
        });
    };
    /**
     * Resolve pending transaction promise (either fulfill or reject with error).
     * Returns true when the given sequence number was actually found.
     */
    BaseClient.prototype._release = function (seqNr, err, msg) {
        var resolver = this._transactions[seqNr];
        if (!resolver) {
            return false;
        }
        delete this._transactions[seqNr];
        if (err) {
            resolver(ts_promise_1.default.reject(err));
        }
        else {
            resolver(msg);
        }
        return true;
    };
    /**
     * Compute next available sequence number.
     * Throws an error when no sequence number is available (too many
     * pending transactions).
     */
    BaseClient.prototype._nextSeq = function () {
        var maxIteration = MAX_SEQ;
        while (--maxIteration > 0 && this._transactions[this._seqNo]) {
            this._seqNo = (this._seqNo + 1) % MAX_SEQ;
        }
        assert(maxIteration, "out of sequence numbers");
        var result = this._seqNo;
        this._seqNo = (this._seqNo + 1) % MAX_SEQ;
        return result;
    };
    return BaseClient;
}(events.EventEmitter));
exports.BaseClient = BaseClient;
exports.default = BaseClient;
//# sourceMappingURL=baseclient.js.map